import React, { useState, useEffect } from 'react';
import './Mark.css';
import axios from 'axios';

function Mark() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoursData, setHoursData] = useState({}); // { "2026-01-26": "8.5", ... }
  const [loading, setLoading] = useState(false);
  const [showWeeklySummary, setShowWeeklySummary] = useState(true);

  const API_BASE = 'https://cfpapi-6ab062006fa0.herokuapp.com/mark';

  useEffect(() => {
    const loadHours = async () => {
      try {
        const response = await axios.post(`${API_BASE}/hours`);
        const stringData = {};
        Object.entries(response.data).forEach(([date, hours]) => {
          stringData[date] = hours.toString();
        });
        setHoursData(stringData);
        setLoading(false);
      } catch (err) {
        console.error('Load error:', err);
        setLoading(false);
      }
    };
    loadHours();
  }, []);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const changeMonth = (delta) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentMonth(newDate);
  };

  const getDateKey = (day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleHoursChange = async (day, value) => {
    if (value === '' || (!isNaN(value) && Number(value) >= 0)) {
      const dateKey = getDateKey(day);
      setHoursData(prev => ({ ...prev, [dateKey]: value }));

      const payload = {
        date: dateKey,
        hours: value.trim() === '' ? 0 : Number(value)
      };

      await axios.post(`${API_BASE}/add`, payload)
        .then(res => console.log('Saved:', res.data))
        .catch(err => console.error('Save error:', err));
    }
  };

  const getHoursForDay = (day) => {
    const key = getDateKey(day);
    return hoursData[key] ?? '';
  };

  const hasHours = (day) => {
    const hrs = getHoursForDay(day);
    return hrs !== '' && !isNaN(Number(hrs)) && Number(hrs) > 0;
  };

  const totalHours = Object.values(hoursData)
    .filter(v => v !== '' && !isNaN(Number(v)))
    .reduce((sum, v) => sum + Number(v), 0);

  const hoursLeft = 680 - totalHours;
  const hoursLeftDisplay = hoursLeft > 0 ? hoursLeft.toFixed(2) : '0';

  // Format date range as MM/DD/YYYY – MM/DD/YYYY
  const formatDateRange = (startStr, endStr) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const options = { month: '2-digit', day: '2-digit', year: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} – ${end.toLocaleDateString('en-US', options)}`;
  };

  // Get start of week (Sunday) for any date
  const getWeekStart = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    const diff = day === 0 ? 0 : day;
    d.setDate(d.getDate() - diff);
    return d.toISOString().split('T')[0];
  };

  // Generate full weeks (Sun–Sat) that overlap the current month
  const getFullWeekRanges = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let currentStart = new Date(getWeekStart(firstDay.toISOString().split('T')[0]));
    const ranges = [];

    while (currentStart <= lastDay) {
      const weekStart = new Date(currentStart);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      if (weekEnd >= firstDay && weekStart <= lastDay) {
        ranges.push({
          start: weekStart.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0]
        });
      }

      currentStart.setDate(currentStart.getDate() + 7);
    }

    console.log("Generated full weeks (Sun–Sat):", ranges.map(r => r.start + " → " + r.end));

    return ranges
      .filter(range => {
        let hasHours = false;
        let d = new Date(range.start);
        while (d <= new Date(range.end)) {
          const key = d.toISOString().split('T')[0];
          const hrs = Number(hoursData[key] || 0);
          if (hrs > 0) {
            hasHours = true;
            break;
          }
          d.setDate(d.getDate() + 1);
        }
        return hasHours;
      })
      .map(r => {
        let sum = 0;
        const logged = [];

        let d = new Date(r.start);
        while (d <= new Date(r.end)) {
          const key = d.toISOString().split('T')[0];
          const hrs = Number(hoursData[key] || 0);
          if (hrs > 0) {
            sum += hrs;
            logged.push(key);
          }
          d.setDate(d.getDate() + 1);
        }

        return {
          range: formatDateRange(r.start, r.end),
          total: sum.toFixed(2),
          dates: logged.join(', ') || '—'
        };
      });
  };

  const daysArray = [];
  for (let i = 0; i < firstDayOfMonth; i++) daysArray.push(null);
  for (let d = 1; d <= daysInMonth; d++) daysArray.push(d);

  if (loading) {
    return <div className="Mark">Loading hours from database...</div>;
  }

  return (
    <div className="Mark" data-month={monthNames[month]}
  data-year={year}>
      
      <div className="stats">
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-label">Total Hours</div>
            <div className="stat-value">{totalHours.toFixed(2)}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Hours Remaining</div>
            <div className={`stat-value ${hoursLeft > 0 ? 'remaining' : 'completed'}`}>
              {hoursLeft > 0 ? hoursLeftDisplay : 'Goal Reached!'}
            </div>
          </div>
        </div>
        <p className="days-logged">
          Days logged: <strong>{Object.keys(hoursData).length}</strong>
        </p>
      </div>
      <div className="calendar-header">
        <button onClick={() => changeMonth(-1)}>← Prev</button>
        <h2>{monthNames[month]} {year}</h2>
        <button onClick={() => changeMonth(1)}>Next →</button>
      </div>

      <div className="calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="weekday">{d}</div>
        ))}

        {daysArray.map((day, idx) => day === null ? (
          <div key={`empty-${idx}`} className="day empty" />
        ) : (
          <div
            key={day}
            className={`day ${hasHours(day) ? 'has-hours' : ''}`}
            style={hasHours(day) ? { backgroundColor: '#d4f4dd' } : {}}
          >
            <div className="day-number">{day}</div>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              maxLength={5}
              value={getHoursForDay(day)}
              onChange={e => handleHoursChange(day, e.target.value)}
              placeholder="hrs"
            />
          </div>
        ))}
      </div>


      {/* Full Week Summary Section */}
{/* Full Week Summary Section */}
{/* Full Week Summary Section – simplified for print testing */}
<div className="weekly-summary-wrapper">
  <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>Weekly Hours Summary – {monthNames[month]} {year}</h3>
  
  {getFullWeekRanges().length === 0 ? (
    <p style={{ textAlign: 'center', fontStyle: 'italic', padding: '20px' }}>
      No hours logged in any full week this month yet.
    </p>
  ) : (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#f0f0f0' }}>
          <th style={{ padding: '12px', border: '1px solid #000' }}>Week Range (Sun–Sat)</th>
          <th style={{ padding: '12px', border: '1px solid #000' }}>Total Hours</th>
        </tr>
      </thead>
      <tbody>
        {getFullWeekRanges().map((p, index) => (
          <tr key={index}>
            <td style={{ padding: '12px', border: '1px solid #000', textAlign: 'left' }}>
              {p.range}
            </td>
            <td style={{ padding: '12px', border: '1px solid #000', fontWeight: 'bold', textAlign: 'left' }}>
              {p.total}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )}
  
  <p style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.9em', color: '#666' }}>
    Generated on {new Date().toLocaleDateString()}
  </p>
</div>
    </div>
  );
}

export default Mark;